// Grid-only number/amount formatters. Sole consumers are the Documents and Bank Feed
// grids' valueFormatters — moved out of the lib/helpers grab-bag where they sat untyped.

type Formattable = string | number | null | undefined;

// Inserts en-US thousands separators ("1234.56" → "1,234.56"). Non-string/number values
// pass through untouched (callers may also short-circuit empty cells upstream).
export const splitNumberByCommas = (value: Formattable): Formattable =>
  typeof value === 'number' || typeof value === 'string' ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : value;

// Coerces a numeric value to a fixed 2-decimal string ("945" → "945.00"). Empty string,
// non-numeric, and non-string/number values pass through unchanged.
// `!Number.isNaN(Number(value))` is exactly the spec definition of the global `isNaN`
// (which the original used under an eslint-disable) — same behaviour, no suppressed global.
export const convertToFloatNumber = (value: Formattable): Formattable => {
  const n = typeof value !== 'object' && !Number.isNaN(Number(value)) && value !== '' ? Number(value) : value;
  return typeof n === 'number' ? n.toFixed(2) : value;
};

// Grid money formatter: numeric value → en-US "1,234.56". This composition was duplicated
// across both grids' amount valueFormatters. For string|number input the helpers' passthrough
// branches (null/undefined) never fire, so the result is always a string — narrow for the type.
export const formatAmount = (value: string | number): string => {
  const formatted = splitNumberByCommas(convertToFloatNumber(value));
  return typeof formatted === 'string' ? formatted : `${formatted}`;
};
